/* Method 3 (secure file transfer): template descriptor, template download, batch status.
   Hand-typed until the file lane's schema.d.ts regenerates these (m4-rulings.md #59). */

import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { apiBlob, apiGet } from "./client";
import type { ConnectionCard } from "./types";

export type FileBatchState =
  | "seen"
  | "claimed"
  | "accepted"
  | "partially_accepted"
  | "rejected"
  | "duplicate";

export type FileBatch = {
  id: string;
  filename: string;
  sha256: string;
  rows: number;
  state: FileBatchState;
  templateVersion: string | null;
  detail: string | null;
  createdAt: string;
  updatedAt: string;
};

export type FileTemplateDescriptorFile = {
  name: string;
  key: string;
  keyColumn: string;
  columns: string[];
  rowsPerDocument: string;
};

export type FileTemplateDescriptor = {
  templateVersion: string;
  files: FileTemplateDescriptorFile[];
  dropPath: string;
  resultsPath: string;
};

/** `GET /api/entities/{id}/connection` gains `fileBatches` for a file entity (m4-rulings.md #59). */
type ConnectionWithBatches = ConnectionCard & { fileBatches: FileBatch[] | null };

const BATCH_POLL = 15000;

/** Polls the connection card and reads its `fileBatches` field; there is no separate batches endpoint. */
export function useFileBatches(entityId: string, enabled = true): UseQueryResult<FileBatch[]> {
  return useQuery({
    queryKey: ["entities", entityId, "connection", "file-batches"],
    queryFn: async () => {
      const card = await apiGet<ConnectionWithBatches>(`/api/entities/${entityId}/connection`);
      return card.fileBatches ?? [];
    },
    enabled,
    refetchInterval: BATCH_POLL,
  });
}

export function useFileTemplateDescriptor(
  entityId: string,
  enabled = true,
): UseQueryResult<FileTemplateDescriptor> {
  return useQuery({
    queryKey: ["entities", entityId, "file-template", "descriptor"],
    queryFn: () => apiGet<FileTemplateDescriptor>(`/api/entities/${entityId}/file-template/descriptor`),
    enabled,
  });
}

/** The zip is fetched as a blob on click, not eagerly; the caller builds and revokes the object URL. */
export function useFileTemplate(entityId: string): UseMutationResult<Blob> {
  return useMutation({
    mutationFn: () => apiBlob(`/api/entities/${entityId}/file-template`),
  });
}
